<script setup lang="ts">
import { computed, ref } from 'vue'

import MediaImage from '@fondof-ui/components/media/MediaImage.vue'
import MediaRoot from '@fondof-ui/components/media/MediaRoot.vue'
import { contentfulUrl } from '@internal/format-contentful'
import Button from '@satch-ui/components/button/Button.vue'
import HeroContent from '@storefront-satch/components/molecules/hero/HeroContent.vue'
import HeroRoot from '@storefront-satch/components/molecules/hero/HeroRoot.vue'
import type { BrandHero } from '@storefront-satch/components/molecules/hero/types'
import MediaVideo from '@storefront-satch/components/molecules/media/MediaVideo.vue'
import { clsx } from 'clsx'

import { ArrowRightIcon, PlayIcon } from '@fond-of/satch-icons'

interface HeroFullProps extends BrandHero {
  /**
   * The Media type is used to differentiate between video and image to enable certain behaviours
   */
  mediaType?: 'image' | 'video' | 'additionalVideo' | null
}

const props = defineProps<HeroFullProps>()
const hasAdditionalImage = !!props.additionalImage
const isVideoPlaying = ref<boolean>(false)

const isLinkElement = computed(() => {
  return props.link && !isVideoWithoutAutoplay.value
})

const isVideoWithoutAutoplay = computed(() => {
  return props.mediaType === 'video' && !props.media?.videoAutoplay
})

const hasVideo = computed(() => {
  return props.mediaType === 'video' || props.mediaType === 'additionalVideo'
})
</script>
<template>
  <HeroRoot
    v-bind="props"
    data-label="Hero"
    :hero-id="props.sys?.id"
    :class="
      clsx({
        'max:h-[calc(theme(screens[2xl])+0.281)] sm:h-[34vw] md:h-[28.1vw]':
          !hasAdditionalImage,
        'max:h-[calc(theme(screens[2xl])+0.281+theme(space.20))] sm:h-[calc(28.1vw+theme(space.10))] md:sm:h-[calc(28.1vw+theme(space.15))] lg:sm:h-[calc(28.1vw+theme(space.20))]':
          hasAdditionalImage,
      })
    "
    :has-additional-image="hasAdditionalImage"
  >
    <component
      :is="isLinkElement ? 'a' : 'div'"
      data-label="Hero Inner"
      :href="isLinkElement ? contentfulUrl(props.link) : undefined"
      :class="[
        'gap-7.5 relative flex size-full flex-col flex-nowrap justify-between',
        'sm:flex-row sm:items-center sm:justify-between sm:gap-0',
      ]"
      :data-gtm-attr="isLinkElement ? 'button_hero' : undefined"
      :data-gtm-label="isLinkElement ? props.internalTitle : undefined"
      :aria-label="
        isLinkElement
          ? props.linkAriaLabel?.trim() || props.linkText?.trim() || undefined
          : undefined
      "
    >
      <div
        v-if="props.media"
        data-label="Hero Image"
        :class="
          clsx(
            'relative aspect-[4/3] size-full self-start',
            'sm:order-2 sm:aspect-auto sm:w-1/2',
            'lg:w-3/5',
            {
              'mb-10 sm:right-0 sm:mb-0 sm:h-[28.1vw] 2xl:h-[calc(theme(screens[2xl])*0.281)]':
                hasAdditionalImage,
            }
          )
        "
      >
        <MediaRoot
          :entry="props.media"
          class="media absolute left-0 top-0 size-full object-cover object-top sm:object-left-top"
        >
          <template #video="{ data }">
            <MediaVideo
              v-bind="data"
              class="size-full"
              @update:video-playing="
                (state) => {
                  isVideoPlaying = state
                }
              "
            />
          </template>
          <template #image="{ data }">
            <MediaImage
              v-bind="data"
              :class="[
                'size-full sm:object-[top_left]',
                {
                  'sm:object-cover': !hasAdditionalImage,
                },
              ]"
              :divider-mobile="2"
              :divider-tablet="1.5"
              :divider-desktop="1"
              lazyload="eager"
              fetchpriority="high"
            />
          </template>
        </MediaRoot>
      </div>
      <div
        data-label="content wrapper"
        :class="[
          'relative w-full pl-5 pr-5',
          'sm:w-[calc(50%-theme(space.5))] sm:pl-10 sm:pr-0',
          'md:w-[calc(50%-theme(space.10))]',
          'lg:pl-15 lg:w-[calc(40%-theme(space.15))]',
          'xl:w-[calc(40%-theme(space.20))]',
          {
            'relative order-2 sm:order-none': hasAdditionalImage,
          },
        ]"
      >
        <div
          v-if="props.content"
          :class="[
            'select-none transition-opacity duration-100',
            {
              'opacity-1': mediaType === 'image',
              'cursor-pointer':
                mediaType === 'video' && !props.media?.videoAutoplay,
              'opacity-0':
                mediaType === 'video' &&
                isVideoPlaying === true &&
                !props.media?.videoAutoplay,
            },
          ]"
        >
          <HeroContent
            :content="props.content"
            :content-style="props.contentStyle"
            :has-additional-image="hasAdditionalImage"
            :headline-size="props.headlineSize"
            :headline-style="props.headlineStyle"
            :class-headline="
              clsx({
                'text-xl': props.headlineSize !== 'super',
                'text-4xl': props.headlineSize === 'super',
              })
            "
          />
          <div
            v-if="props.linkStyle && props.linkText"
            class="mt-4 w-full"
          >
            <Button
              v-if="props.linkText && (props.link || isVideoWithoutAutoplay)"
              class="font-sans-medium font-semibold"
              :variant="props.linkStyle"
              padding="large"
              text-size="large"
            >
              {{ props.linkText }}
              <PlayIcon
                v-if="isVideoWithoutAutoplay && hasVideo"
                class="w-4"
              />
              <ArrowRightIcon
                v-else
                class="w-4"
              />
            </Button>
          </div>
        </div>
      </div>
      <div
        v-if="hasAdditionalImage"
        :class="[
          'absolute left-1/2 right-[calc(75vw-(100vw-100px))] z-[1] aspect-square w-[calc(100%-theme(space.15))] -translate-x-1/2',
          'xs:top-[calc(75vw-(100vw-140px))] xs:w-[calc(100%-100px)]',
          'sm:left-auto sm:top-10 sm:h-[calc(100%-theme(space.10))] sm:w-auto sm:translate-x-0',
          'sm:right-15 lg:right-25 md:right-20',
        ]"
      >
        <MediaRoot
          :entry="props.additionalImage"
          class="relative size-full"
        >
          <template #video="{ data }">
            <MediaVideo
              v-bind="data"
              class="size-full"
              @update:video-playing="
                (state) => {
                  isVideoPlaying = state
                }
              "
            />
          </template>
          <template #image="{ data }">
            <MediaImage
              v-bind="data"
              class="w-full"
              :divider-mobile="2"
              :divider-tablet="2"
              :divider-desktop="1.5"
              lazyload="eager"
              fetchpriority="high"
            />
          </template>
        </MediaRoot>
      </div>
    </component>
  </HeroRoot>
</template>
